import type { AdditionalDetailsPostingProps } from '@tempmee/core';
import { IJobPostDetailEntity } from '~/domain/PermanentJobs';
import { EducationUtils } from '~/utils/EducationUtils';
import { EM_DASH } from '~/utils/String';
import {
  formatBenefits,
  formatExpectedDays,
  formatPayRange,
} from '../JobDetailsForm/utils/formatters';

/** The eight row values of `AdditionalDetails variant="posting"`; the block owns the labels. */
export type TPostingAdditionalDetails = Pick<
  AdditionalDetailsPostingProps,
  | 'pay'
  | 'payType'
  | 'speciality'
  | 'postedBy'
  | 'benefits'
  | 'otherBenefits'
  | 'schedule'
  | 'expectedDays'
>;

type TPostingFields = Pick<
  IJobPostDetailEntity,
  | 'minRate'
  | 'maxRate'
  | 'payType'
  | 'officeSpecialty'
  | 'benefits'
  | 'benefitsOther'
  | 'scheduleType'
  | 'expectedDays'
>;

/**
 * A posting's Additional Information values, formatted the way the posting review screens
 * already format them. Every row is drawn even when empty — an em dash keeps the two-column
 * grid aligned. "Posted by" is always an em dash: the posting only carries the creator's
 * user id, not a name.
 */
export const toPostingAdditionalDetailsProps = (
  posting: TPostingFields,
): TPostingAdditionalDetails => ({
  pay: formatPayRange(posting.minRate, posting.maxRate, posting.payType) || EM_DASH,
  payType: posting.payType || EM_DASH,
  speciality: EducationUtils.removeSpecialtyPrefix(posting.officeSpecialty || '') || EM_DASH,
  postedBy: EM_DASH,
  benefits: formatBenefits(posting.benefits || []) || EM_DASH,
  otherBenefits: posting.benefitsOther || EM_DASH,
  schedule: posting.scheduleType || EM_DASH,
  expectedDays: formatExpectedDays(posting.expectedDays || [], { abbreviated: true }) || EM_DASH,
});
